/**
 * ProcessingPipelineService — [p] in the [we]/[ecs] → [p] → [s] architecture.
 *
 * Processes arbitrary collections of TextBlocks grouped by elId.  It has no
 * concept of "a document" as a structural unit — elId is simply the key that
 * groups blocks for display and session-resume purposes.  Within that group
 * every block (paragraph) is identified and processed independently by srcId.
 *
 * The same paragraph exists in two projections:
 *   TextBlock    — editor side: srcId + seqId + plain text.  [e] owns this.
 *   paragraphDTO — pipeline side: srcId + seqId + full spelling state.  [p] owns this.
 * srcId is the shared identity.  seqId lets [p] skip unchanged blocks and
 * discard stale trie/server results without string comparison.
 *
 * Responsibilities:
 *   - Diffs incoming TextBlocks against stored paragraphDTOs (O(n) by srcId/seqId).
 *   - Preserves spelling state for unchanged content; marks changed content new.
 *   - Emits updated state immediately on every change (display stays current).
 *   - Queues paragraphs with new/local content for trie + server lookup.
 *   - Merges trie and server results back by srcId+seqId; discards stale results.
 *
 * The documentDTO stored and emitted here is NOT the processing unit — it is
 * the display-layer contract and the session-resume envelope.  The processing
 * unit is paragraphDTO, identified by srcId.
 *
 * No editor-specific knowledge. No Angular display concerns.
 *
 * See docs/CSW-1354-rearchitect-for-common-text-and-display-pipelines.md
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, filter, map, startWith } from 'rxjs';

import {
  DocumentSnapshot,
  TextBlock,
  documentDTO,
  editStateEnum,
  spellingStateEnum,
  MultiElementDocumentStore,
  Paragraph,
  paragraphDTO,
  sentenceDTO,
  tokenDTO,
  DocumentStore,
} from '@capiche/ts-data-models-shared';

import { preserveParagraphState, computeChangeRange, deriveEditStateFromTokens, deriveEditStateFromSentences } from './preserve-paragraph-state';

import { CapicheLoggerService } from 'capiche-logger';
import { TextProcessorService } from '../text-processor/text-processor.service';

/**
 * Emitted by the pipeline after each processing step.
 *
 * `doc` is the display-layer contract and session-resume envelope — it wraps
 * the current paragraphDTO[] for this elId.  It is NOT the processing unit;
 * individual paragraphs (identified by srcId) are.
 */
export interface PipelineResult {
  elId: string;
  doc: documentDTO;
}

/**
 * Instruction emitted by [p] to [e] to execute a text substitution.
 *
 * Carries semantic identity (srcId + sentence/token ordinals) — NOT a
 * character offset.  [e] is responsible for resolving the current document
 * position from its own live node/position tracking.
 *
 * tokenIndex === null means replace the entire sentence (sentence replacement).
 */
export interface ReplacementInstruction {
  elId: string;
  srcId: string;
  /** 0-based ordinal of the sentence within the paragraph. */
  sentenceIndex: number;
  /** 0-based ordinal of the token within the sentence.  null = whole sentence. */
  tokenIndex: number | null;
  replacementText: string;
}

/**
 * Internal change descriptor — produced by diffSnapshot(), consumed by applyChanges().
 * Not part of the public API.
 */
interface BlockChange {
  srcId: string /** Stable block identity (from TextBlock.srcId). */;
  seqId: number /** Sequence counter at the time of this change. */;
  priorInput: string | null /** Block text before the change (null = brand-new block). */;
  postInput: string | null /** Block text after the change (null = block was deleted). */;
  index: number /** Document position for ordering / server context. */;
}

@Injectable({
  providedIn: 'root',
})
export class ProcessingPipelineService implements OnDestroy {
  private readonly logger = new CapicheLoggerService('processing-pipeline.service:', true);

  /** Canonical state store — one DocumentStore per tracked element ID. */
  private readonly stateStore: MultiElementDocumentStore = new MultiElementDocumentStore();

  /**
   * Pending replacement srcIds: elId → Set of srcIds.
   * Populated by requestReplacement(); consumed and cleared in process().
   * Tells applyPendingReplacements() to mark the dirty region as replaced
   * rather than new when the resulting snapshot arrives.
   */
  private readonly pendingReplacements = new Map<string, Set<string>>();

  /** Emits every time processed state changes for any element. */
  private readonly resultSubject = new Subject<PipelineResult>();
  public readonly result$: Observable<PipelineResult> = this.resultSubject.asObservable();

  /** Emits replacement instructions for [e] to execute. */
  private readonly replacementInstructionSubject = new Subject<ReplacementInstruction>();
  public readonly replacementInstruction$: Observable<ReplacementInstruction> =
    this.replacementInstructionSubject.asObservable();

  private readonly subs = new Subscription();

  constructor(private readonly textProcessor: TextProcessorService) {
    this.logger.log('constructor');
    // Route local trie results back into the store and downstream.
    this.subs.add(
      this.textProcessor.localOutput$.subscribe(({ elId, para }) => {
        this.mergeResult(elId, para);
      }),
    );

    // Route server (Phase 1 + Phase 2) results back into the store and downstream.
    this.subs.add(
      this.textProcessor.serverOutput$.subscribe(({ elId, para }) => {
        this.mergeResult(elId, para);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Accept a DocumentSnapshot from an editor.
   *
   * 1. Diffs the snapshot against the canonical store (O(n) by srcId/seqId).
   * 2. Applies changed blocks, preserving spelling state for unchanged content.
   * 3. Emits the updated document immediately (so the display reflects typing).
   * 4. Queues changed paragraphs for lookup (trie + server) for all triggers.
   */
  process(snapshot: DocumentSnapshot): void {
    const store = this.stateStore.getOrCreate(snapshot.elId);
    const currentDoc = store.get();

    const changes: BlockChange[] = this.diffSnapshot(currentDoc.paragraphs, snapshot.blocks, snapshot.partial);
    this.logger.log(`process() elId=${snapshot.elId} trigger=${snapshot.trigger} changes=${changes.length}`);

    // Fetch pending replacements up-front, outside the changes guard.
    // They must be drained on EVERY snapshot — even when changes.length === 0.
    // If the replacement text was identical to the original (e.g. bee→bee, where
    // the top suggestion is the same word), diffSnapshot finds no changes and the
    // inner block is skipped, leaving the pending entry in the map.  The entry
    // then leaks into the next unrelated snapshot, incorrectly marking subsequent
    // user edits (beee) as replaced.
    const pendingSrcIds = this.pendingReplacements.get(snapshot.elId);

    // Only update the store and emit when the content actually changed.
    // Processing tiers still run even with 0 changes — paragraphs already in the
    // store may still be in editState.new / editState.local and need server lookup.
    if (changes.length > 0) {
      let updatedParagraphs = this.applyChanges(currentDoc.paragraphs, changes);

      // If any changed blocks have a pending replacement notification, mark the
      // tokens in their dirty regions as replaced rather than new.
      if (pendingSrcIds?.size) {
        updatedParagraphs = this.applyPendingReplacements(updatedParagraphs, changes, pendingSrcIds);
      }

      // Sync ProseMirror positions for blocks whose content did not change but
      // whose document position shifted because a preceding block changed length
      // (e.g. the user pressed Enter, splitting a paragraph).  Unchanged blocks
      // are absent from changes[] so applyChanges() never touches their index.
      // Without this, highlight.service calculates `para.index + 1 + ...` using a
      // stale node position, shifting every decoration by the accumulated delta.
      updatedParagraphs = this.syncParagraphIndices(updatedParagraphs, snapshot.blocks);

      const updatedDoc: documentDTO = {
        ...currentDoc,
        editState: editStateEnum.new,
        paragraphs: updatedParagraphs,
      };
      store.update(updatedDoc);
      this.resultSubject.next({ elId: snapshot.elId, doc: updatedDoc });
    }

    // Always drain pending replacements — applyImmediateReplacementState already
    // applied the token state; this map only needs to survive until the matching
    // snapshot arrives.  Clear unconditionally so stale entries never pollute
    // subsequent snapshots.
    pendingSrcIds?.clear();

    // Read paragraphs from the store so processing tiers always see the latest state,
    // whether or not the content changed in this snapshot.
    const currentParagraphs: paragraphDTO[] = store.get().paragraphs;

    // All triggers run trie + server; pipeline gates redundant work via seqId/editState.
    // CSW-1736: a paragraph whose request (at this seqId) is still in flight is not
    // re-issued — on a long document every snapshot was otherwise re-sending every
    // pending paragraph, not just the one that changed.  It is re-evaluated on the
    // next snapshot once its response has landed (or it has been edited: new seqId).
    const toProcess: paragraphDTO[] = currentParagraphs.filter(
      (p: paragraphDTO) =>
        this.requiresTextProcessing(p) && !this.textProcessor.isInFlight(snapshot.elId, p.srcId, p.seqId, p.input),
    );
    toProcess.forEach((p: paragraphDTO) => this.textProcessor.processInput(p, snapshot.elId));
  }

  /**
   * Seed the store for `elId` with a previously-computed documentDTO, but
   * only if the pipeline has not already seen this elId in this instance.
   *
   * CSW-1466: [p] lives entirely inside whatever JS context owns this
   * service instance — for the Chrome extension side panel, that context is
   * destroyed on every panel close, taking every bit of computed spelling
   * state with it. A caller that persists documentDTOs somewhere that
   * survives context teardown (e.g. chrome.storage.session) can restore one
   * here, before the first process() call for that elId, so process()'s own
   * diffSnapshot() naturally finds zero changes for unchanged content —
   * instead of reprocessing everything from scratch on every reopen.
   *
   * Deliberately declines (returns false, does nothing) if a store already
   * exists for elId: hydration is a startup-only concern for restoring
   * state this instance never had, not a way to overwrite state this
   * instance has already computed live.
   *
   * DOES emit on result$ (correction — an earlier version of this doc said
   * it deliberately didn't; that was wrong in practice, found via manual
   * testing: a hydrated-then-matching process() call produces zero
   * diffSnapshot() changes and so wouldn't emit either, but result$ has
   * TWO independent consumers in the Chrome extension, not one —
   * TextProcessorCoordinator's content-script routing AND
   * AppPageComponent's own display subscription (its side panel suggestion
   * list). The original design only had the caller manually push the
   * routing half, leaving the panel's own list stuck empty until some
   * unrelated later event (e.g. a tab switch re-triggering FOCUS_CHANGE)
   * happened to read getDoc() again. Emitting here means every result$
   * subscriber — present or future — sees a rehydration exactly like any
   * other state change, with no caller-side special-casing required.
   */
  hydrate(elId: string, doc: documentDTO): boolean {
    if (this.stateStore.has(elId)) return false;
    this.stateStore.getOrCreate(elId, doc);
    this.logger.log(`hydrate: seeded elId=${elId} with ${doc.paragraphs.length} paragraph(s) from a persisted cache`);
    this.resultSubject.next({ elId, doc });
    return true;
  }

  /**
   * Return the current state envelope for the given element, or undefined if
   * the element has never been seen by the pipeline.
   *
   * The returned documentDTO is the display-layer contract and session-resume
   * envelope.  To inspect or modify individual paragraphs, iterate
   * doc.paragraphs and match by srcId.
   */
  getDoc(elId: string): documentDTO | undefined {
    return this.stateStore.get(elId)?.get();
  }

  /**
   * True if calling process() with these blocks for this elId would find
   * zero changes — i.e. would NOT update the store or emit on result$.
   *
   * Runs the exact same srcId/seqId-based diffSnapshot() logic process()
   * itself uses to decide that, non-mutating. Exists so callers who need to
   * anticipate "will a fresh result arrive" (e.g. deciding whether to
   * eagerly clear a stale display before reprocessing) ask the pipeline
   * directly instead of inventing their own comparison against a different,
   * possibly out-of-sync signal.
   *
   * CSW-1400: the case that motivated this — app-page.component.ts's
   * paste/cut handler compared `getDoc(elId)?.input` against the pasted
   * text to guess whether the pipeline would re-emit. That never worked:
   * documentDTO.input is set once, on initial (empty-string) seed creation
   * in MultiElementDocumentStore.getOrCreate(), and process() never updates
   * it thereafter (it only spreads editState/paragraphs) — so the field is
   * permanently '', and the comparison was always false. The guard silently
   * did nothing; the stuck-blank-on-identical-paste bug it was meant to fix
   * was never actually fixed. Use this method instead, which reads the same
   * srcId/seqId state process() itself decides from.
   *
   * Must be called before process() for the same elId+snapshot, since
   * process() is what mutates the store this compares against.
   */
  wouldSkip(elId: string, blocks: ReadonlyArray<TextBlock>, partial: boolean = false): boolean {
    const store = this.stateStore.get(elId);
    if (!store) return false; // never seen before — process() would create it as new, not skip
    return this.diffSnapshot(store.get().paragraphs, blocks, partial).length === 0;
  }

  /**
   * Observable form of getDoc — emits the current document immediately
   * (BehaviorSubject-like semantics) and then on every subsequent change.
   *
   * The primary subscription point for display consumers ([s]).  Replaces the
   * pattern of subscribing to result$ and maintaining a separate copy of state.
   * There is only one copy of the state: the pipeline's MultiElementDocumentStore.
   */
  getDoc$(elId: string): Observable<documentDTO> {
    return this.result$.pipe(
      filter((r) => r.elId === elId),
      map((r) => r.doc),
      startWith(this.stateStore.get(elId)?.get()),
      filter((d): d is documentDTO => d !== undefined),
    );
  }

  /**
   * Remove state for an element (e.g. when the element loses focus permanently
   * or the extension detects it has been removed from the DOM).
   */
  removeElement(elId: string): void {
    this.stateStore.delete(elId);
  }

  /**
   * Signal a token replacement from [ux].
   *
   * 1. Immediately marks the token editState.replaced / spellingState.correct
   *    in the store and emits updated state to [s] (instant UI feedback).
   * 2. Queues the srcId so the next snapshot for this paragraph is treated as
   *    a replacement (not a user edit) — preventing re-processing.
   * 3. Emits a ReplacementInstruction on replacementInstruction$ for [e] to
   *    execute the text substitution.  [e] resolves the current document
   *    position itself; [p] never passes a character offset.
   *
   * @param elId            Element identifier.
   * @param srcId           Block (paragraph) identity.
   * @param sentenceIndex   0-based ordinal of the sentence within the paragraph.
   * @param tokenIndex      0-based ordinal of the token within the sentence.
   * @param replacementText The text to substitute.
   */
  requestReplacement(
    elId: string,
    srcId: string,
    sentenceIndex: number,
    tokenIndex: number,
    replacementText: string,
  ): void {
    this.logger.log(
      `requestReplacement: elId=${elId} srcId=${srcId} s=${sentenceIndex} t=${tokenIndex} text="${replacementText}"`,
    );
    this.applyImmediateReplacementState(elId, srcId, sentenceIndex, tokenIndex, null);
    this.queuePendingReplacement(elId, srcId);
    this.replacementInstructionSubject.next({ elId, srcId, sentenceIndex, tokenIndex, replacementText });
  }

  /**
   * Signal a sentence replacement from [ux].
   *
   * Same flow as requestReplacement but marks ALL tokens in the sentence as
   * replaced/correct and instructs [e] to replace the whole sentence text.
   *
   * @param elId            Element identifier.
   * @param srcId           Block (paragraph) identity.
   * @param sentenceIndex   0-based ordinal of the sentence within the paragraph.
   * @param replacementText The full sentence text to substitute.
   */
  requestSentenceReplacement(elId: string, srcId: string, sentenceIndex: number, replacementText: string): void {
    this.logger.log(
      `requestSentenceReplacement: elId=${elId} srcId=${srcId} s=${sentenceIndex} text="${replacementText}"`,
    );
    this.applyImmediateReplacementState(elId, srcId, sentenceIndex, null, null);
    this.queuePendingReplacement(elId, srcId);
    this.replacementInstructionSubject.next({ elId, srcId, sentenceIndex, tokenIndex: null, replacementText });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Immediately update the store with replaced/correct state for a token or
   * sentence, then emit to [s] for instant display feedback.
   *
   * tokenIndex === null means replace all tokens in the sentence.
   * sentenceIndex === null is not used (always required).
   */
  private applyImmediateReplacementState(
    elId: string,
    srcId: string,
    sentenceIndex: number,
    tokenIndex: number | null,
    _unused: null,
  ): void {
    const store = this.stateStore.get(elId);
    if (!store) return;

    const currentDoc = store.get();
    const updatedParagraphs = currentDoc.paragraphs.map((para) => {
      if (para.srcId !== srcId) return para;

      const sentence = para.sentences[sentenceIndex];
      if (!sentence) return para;

      const updatedTokens: tokenDTO[] = sentence.tokens.map((tok, tIdx) => {
        if (tokenIndex !== null && tIdx !== tokenIndex) return tok;
        return { ...tok, editState: editStateEnum.replaced, spellingState: spellingStateEnum.correct, suggestions: [] };
      });

      const updatedSentence: sentenceDTO = {
        ...sentence,
        tokens: updatedTokens,
        editState: deriveEditStateFromTokens(updatedTokens),
      };

      const updatedSentences = para.sentences.map((s, sIdx) => (sIdx === sentenceIndex ? updatedSentence : s));

      return { ...para, sentences: updatedSentences, editState: deriveEditStateFromSentences(updatedSentences) };
    });

    const updatedDoc: documentDTO = { ...currentDoc, paragraphs: updatedParagraphs };
    store.update(updatedDoc);
    this.resultSubject.next({ elId, doc: updatedDoc });
    this.logger.log(`applyImmediateReplacementState: elId=${elId} srcId=${srcId} s=${sentenceIndex} t=${tokenIndex}`);
  }

  /** Add srcId to the pending replacement queue for elId. */
  private queuePendingReplacement(elId: string, srcId: string): void {
    let pending = this.pendingReplacements.get(elId);
    if (!pending) {
      pending = new Set<string>();
      this.pendingReplacements.set(elId, pending);
    }
    pending.add(srcId);
    this.logger.log(`queuePendingReplacement: elId=${elId} srcId=${srcId}`);
  }

  /**
   * Diff a DocumentSnapshot against the current paragraph store.
   *
   * Answers one question: what changed between the last known document state
   * and the incoming snapshot?  Decision is based solely on srcId + seqId —
   * the text content is never inspected here.
   *
   *   srcId not in store                        → new      (create from scratch)
   *   srcId in store, seqId same                → unchanged (skip — text identical by contract)
   *   srcId in store, seqId different           → changed  (re-tokenise + preserve state)
   *   srcId in store, absent from snapshot,
   *     partial = false                         → deleted  (remove from store)
   *   srcId in store, absent from snapshot,
   *     partial = true                          → retained (silent no-op; viewport/partial snapshot)
   *
   * No byText or byIndex fallback — eliminated by stable srcIds.
   *
   * @param partial  When true, blocks absent from the snapshot are NOT treated
   *                 as deleted (partial/viewport-scoped snapshot).
   */
  private diffSnapshot(
    prior: paragraphDTO[],
    blocks: ReadonlyArray<TextBlock>,
    partial: boolean = false,
  ): BlockChange[] {
    const t0: number = performance.now();

    const priorById = new Map(prior.map((p) => [p.srcId, p]));
    const postIds = new Set(blocks.map((b) => b.srcId));
    const changes: BlockChange[] = [];

    for (const block of blocks) {
      const prev = priorById.get(block.srcId);
      // seqId match → text is identical by contract → skip
      if (prev && prev.seqId === block.seqId) continue;
      this.logger.info(`diffSnapshot() block changed`, block);
      changes.push({
        srcId: block.srcId,
        seqId: block.seqId,
        priorInput: prev?.input ?? null,
        postInput: block.input,
        index: block.index,
      });
    }

    if (!partial) {
      // Full snapshot: blocks absent from snapshot have been deleted.
      for (const p of prior) {
        if (p.srcId && !postIds.has(p.srcId)) {
          this.logger.info(`diffSnapshot() block deleted`, p);
          changes.push({
            srcId: p.srcId,
            seqId: p.seqId ?? 0,
            priorInput: p.input,
            postInput: null,
            index: p.index,
          });
        }
      }
    }
    this.logger.log(`diffSnapshot(): ${changes.length} changes in ${Math.round(1000 * (performance.now() - t0))}µs`);
    return changes;
  }

  /**
   * Apply a set of BlockChanges to the current paragraph array.
   *
   * Paragraph indexes are not recalculated here — the editor supplies fresh
   * absolute indexes with every DocumentSnapshot, so BlockChange.index is
   * always current.
   *
   * For each change:
   *   deleted  (postInput === null)  — remove from result.
   *   changed  (priorInput !== null) — re-tokenise new text (all tokens start
   *                                    editState.new), then call
   *                                    preserveParagraphState() to restore
   *                                    spelling state for every sentence and
   *                                    token whose text is unchanged.  This is
   *                                    the editor-change merge path (distinct
   *                                    from the server-result merge in
   *                                    mergeResult()).
   *   new      (priorInput === null) — insert the fresh paragraph as-is; no
   *                                    prior state to preserve.
   *
   * Unchanged paragraphs (not in changes[]) are carried forward untouched.
   * Result is sorted by document position (pushes may arrive out of order).
   */
  private applyChanges(current: paragraphDTO[], changes: BlockChange[]): paragraphDTO[] {
    const t0: number = performance.now();
    const byId = new Map(current.map((p) => [p.srcId, p])); // Index current store by srcId for O(1) prior-state lookup.
    let result: paragraphDTO[] = [...current]; // Start with all current paragraphs; deletions and replacements mutate this.

    // for each of BlockChange[]
    for (const change of changes) {
      // if deleted, remove the paragraph from the result.
      if (change.postInput === null) {
        result = result.filter((p) => p.srcId !== change.srcId);
        continue;
      }

      // if not deleted
      const prior: paragraphDTO | undefined = change.priorInput !== null ? byId.get(change.srcId) : undefined; // grab the prior state for this block if it exists

      // re-tokenise the new state from scratch; all tokens start as editState.new.
      const fresh: paragraphDTO = Paragraph.fromString(
        change.postInput,
        change.index,
        change.seqId,
        change.srcId,
      ).toDTO();

      // if prior exists, merge state; else use fresh as-is.
      const merged: paragraphDTO = prior ? preserveParagraphState(prior, fresh) : fresh;

      // CSW-1736: a paragraph with no sentences (blank line) has nothing to look up.
      // Left as editState.new it would be sent to the server on every snapshot
      // forever — the server answers with zero sentences, so mergeResult() finds
      // nothing to merge and never settles it to checked.  Settle it here instead.
      const next: paragraphDTO =
        merged.sentences.length === 0 ? { ...merged, editState: editStateEnum.checked } : merged;

      // Replace in-place if already in result (expected for changed blocks),
      // otherwise push (expected for new blocks; in-place is defensive only).
      const existingIdx: number = result.findIndex((p: paragraphDTO) => p.srcId === change.srcId);
      if (existingIdx !== -1) {
        result[existingIdx] = next;
      } else {
        result.push(next);
      }
    }

    // result.push() may land out of document order — sort by absolute character index.
    result.sort((a, b) => a.index - b.index);
    this.logger.log(`applyChanges(): ${result.length} paragraphs in ${Math.round(1000 * (performance.now() - t0))}µs`);
    return result;
  }

  /**
   * Sync ProseMirror node positions for paragraphs that appear in the snapshot
   * but whose content (seqId) did not change.
   *
   * When a preceding paragraph changes length (e.g. the user presses Enter or
   * deletes characters that straddle a paragraph boundary), every subsequent
   * paragraph node shifts in PM position.  Those paragraphs have an unchanged
   * seqId so diffSnapshot() skips them and applyChanges() never updates their
   * index.  highlight.service.ts computes `para.index + 1 + sent.index + tok.index`
   * to derive absolute PM positions, so a stale index silently misaligns every
   * decoration in the affected paragraphs.
   *
   * This pass touches only paragraphs whose index actually changed, avoiding
   * unnecessary object allocation for the common case where nothing shifted.
   */
  private syncParagraphIndices(paragraphs: paragraphDTO[], blocks: ReadonlyArray<TextBlock>): paragraphDTO[] {
    const freshIndexById = new Map<string, number>(blocks.map((b) => [b.srcId, b.index]));
    return paragraphs.map((p) => {
      const freshIndex = p.srcId ? freshIndexById.get(p.srcId) : undefined;
      if (freshIndex === undefined || freshIndex === p.index) return p;
      this.logger.log(`syncParagraphIndices: srcId=${p.srcId} index ${p.index} → ${freshIndex}`);
      return { ...p, index: freshIndex };
    });
  }

  /**
   * For each paragraph in the pending-replacement set, mark every token whose
   * paragraph-local character span overlaps the dirty region as
   * `editState.replaced / spellingState.correct`.
   *
   * This gives immediate correct display after a suggestion acceptance — no
   * server round-trip required.  The dirty region is `[changeStart, newEnd)`
   * from `computeChangeRange(priorInput, postInput)`.
   *
   * token.index is sentence-local; sentence.index is paragraph-local.
   * Paragraph-local token offset = sentence.index + token.index.
   *
   * editState is re-derived bottom-up after marking tokens.
   */
  private applyPendingReplacements(
    paragraphs: paragraphDTO[],
    changes: BlockChange[],
    pendingSrcIds: Set<string>,
  ): paragraphDTO[] {
    return paragraphs.map((para) => {
      if (!para.srcId || !pendingSrcIds.has(para.srcId)) return para;

      // Find the corresponding change to get priorInput/postInput.
      const change = changes.find((c) => c.srcId === para.srcId && c.postInput !== null);
      if (!change || change.postInput === null) return para;

      const { changeStart, newEnd } = computeChangeRange(change.priorInput ?? '', change.postInput);

      const updatedSentences: sentenceDTO[] = para.sentences.map((sent) => {
        // sent.index is paragraph-local (char offset within paragraph text).
        // para.index is the ProseMirror node position — not in the same space.
        const sentLocalStart = sent.index;

        const updatedTokens: tokenDTO[] = sent.tokens.map((tok) => {
          // Token span is sentence-local; convert to paragraph-local.
          const tokParaStart = sentLocalStart + tok.index;
          const tokParaEnd = tokParaStart + tok.length;

          // Overlaps dirty region [changeStart, newEnd)?
          //
          // Standard case: tokParaEnd > changeStart && tokParaStart < newEnd
          //
          // Trailing-deletion edge case: when a replacement removes characters
          // from the END of a token (e.g. "itt"→"it", "bee"→"be"), the LCP/LCS
          // analysis gives newEnd === changeStart — a zero-length new range.
          // The resulting token ends exactly at changeStart so the standard
          // check (strict >) misses it.  The second condition catches any token
          // whose end touches the deletion point and whose start is before it.
          const trailingDeletion = newEnd === changeStart && tokParaEnd === changeStart && tokParaStart < changeStart;
          if ((tokParaEnd > changeStart && tokParaStart < newEnd) || trailingDeletion) {
            return {
              ...tok,
              editState: editStateEnum.replaced,
              spellingState: spellingStateEnum.correct,
              suggestions: [],
            };
          }
          return tok;
        });

        // If any token in this sentence was replaced, set editState to local rather than
        // deriving from token states.  Replaced tokens derive to 'checked' (they are not
        // new or local), which would make requiresTextProcessing() return false and suppress
        // Phase 2.  Phase 2 is needed to assess contextual corrections on the replacement
        // word — e.g. "with" at the start of a sentence should become contextual ("With").
        // Setting local signals "Phase 1 effectively done, Phase 2 still pending".
        const hasReplaced = updatedTokens.some((t) => t.editState === editStateEnum.replaced);
        const sentEditState = hasReplaced ? editStateEnum.local : deriveEditStateFromTokens(updatedTokens);
        return { ...sent, tokens: updatedTokens, editState: sentEditState };
      });

      const paraEditState = deriveEditStateFromSentences(updatedSentences);

      this.logger.log(
        `applyPendingReplacements: marked replaced tokens in srcId=${para.srcId} ` + `range=[${changeStart},${newEnd})`,
      );
      return { ...para, sentences: updatedSentences, editState: paraEditState };
    });
  }

  /**
   * Merge a processed paragraph result back into the canonical store.
   *
   * Sentence-level merge strategy — "apply if unchanged and newer":
   *
   *   1. Match the result paragraph by srcId.
   *   2. For each sentence in the stored paragraph, look up the corresponding
   *      sentence in the result by sentence.index (paragraph-local position).
   *   3. If sentence.seqId matches → the sentence text was unchanged between
   *      when the query was sent and now → apply the server result for that
   *      sentence.
   *   4. If sentence.seqId differs → the sentence was edited after the query
   *      fired → discard the result for that sentence, keep stored state.
   *
   * This enables partial application: a server response for a two-sentence
   * paragraph can update sentence 1 (unchanged) even while the user is still
   * editing sentence 2 (seqId mismatch → kept as-is).
   *
   * Index preservation: always take the current index from the store — the
   * result may carry a stale index if a preceding block changed length.
   *
   * Phase 1 / Phase 2 semantics are enforced upstream in TextProcessorService
   * before the result is emitted here — this function is phase-agnostic.
   */
  private mergeResult(elId: string, para: paragraphDTO): void {
    const store: DocumentStore | undefined = this.stateStore.get(elId);
    if (!store) {
      this.logger.warn(`mergeResult: no store for elId=${elId} — result discarded`);
      return;
    }

    const currentDoc: documentDTO = store.get();

    // Build a lookup from sentence.index → result sentence for O(1) access.
    const resultSentsByIndex = new Map(para.sentences.map((s) => [s.index, s]));

    let docChanged = false;
    const updatedParagraphs = currentDoc.paragraphs.map((p) => {
      if (p.srcId !== para.srcId) return p;

      let sentenceChanged = false;
      const mergedSentences = p.sentences.map((storedSent) => {
        const resultSent = resultSentsByIndex.get(storedSent.index);
        // No result for this sentence position, or seqId mismatch (sentence was
        // edited after this query was sent) → keep stored sentence unchanged.
        if (!resultSent || storedSent.seqId !== resultSent.seqId) return storedSent;
        sentenceChanged = true;
        // Apply server result; preserve the stored index (result index may be stale).
        return { ...resultSent, index: storedSent.index };
      });

      if (!sentenceChanged) return p;

      docChanged = true;
      return {
        ...p,
        sentences: mergedSentences,
        editState: deriveEditStateFromSentences(mergedSentences),
      };
    });

    if (!docChanged) return;

    const updatedDoc: documentDTO = { ...currentDoc, paragraphs: updatedParagraphs };
    store.update(updatedDoc);
    this.resultSubject.next({ elId, doc: updatedDoc });

    this.logger.log(`mergeResult: elId=${elId} para.srcId=${para.srcId}`);
  }

  /**
   * True if the paragraph needs server processing — either it has brand-new tokens,
   * or it has been through the local trie but not yet server-checked.
   *
   * 'new'   → just typed, neither trie nor server has run
   * 'local' → local trie done, server call still pending
   *
   * Used for debounced / sentence-end full processing.
   *
   * TODO [CSW-1353]: empty paragraphs (input:'', sentences:[]) with seqId:0 pass this
   * check and go through the full Phase 1 + Phase 2 server round-trip for no benefit.
   * These are ProseMirror structural nodes (e.g. trailing empty block). A short-circuit
   * here (para.length === 0 → false) would eliminate the noise, but the right fix is
   * to stop treating PM structural nodes as data paragraphs — part of the documentDTO
   * coordinate-system cleanup in CSW-1353.
   */
  private requiresTextProcessing(para: paragraphDTO): boolean {
    return (
      para.editState === editStateEnum.new ||
      para.editState === editStateEnum.local ||
      para.sentences.some((s) => s.editState === editStateEnum.new || s.editState === editStateEnum.local)
    );
  }
}
